<template>
    <div v-if="showCard" class="min-w-50 flex flex-col">
        <HomeSectionHeader :title="props.title" />
        <BaseCard v-if="tonalThemeEnabled" theme="muted" class="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
            <div class="flex items-center gap-6">
                <GoalSettingMark class="h-15.25 w-10 shrink-0 text-accent" />
                <div class="flex min-w-0 flex-col gap-1 text-ink">
                    <h3 class="nadia-title-subsection">{{ cardTitle }}</h3>
                    <p class="nadia-chat">{{ cardSubtitle }}</p>
                </div>
            </div>

            <div class="flex w-full shrink-0 flex-col gap-2 md:w-auto">
                <BaseLink
                    v-for="moment in liveMoments"
                    :key="moment.id"
                    :href="momentUrl(moment)"
                    class="base-btn-neutral inline-flex w-full items-center justify-center md:w-auto"
                    @click="logCtaClick(moment)">
                    {{ moment.title }}
                </BaseLink>
            </div>
        </BaseCard>
        <BaseCard v-else theme="muted" padding="lg" class="overflow-hidden text-ink">
            <div class="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
                <div class="flex-1">
                    <h3 class="title-body">{{ cardTitle }}</h3>
                    <p class="mt-3 body-small">{{ cardSubtitle }}</p>
                </div>

                <div class="flex w-full flex-col gap-3 md:w-auto md:min-w-65">
                    <BaseLink
                        v-for="moment in liveMoments"
                        :key="moment.id"
                        :href="momentUrl(moment)"
                        class="base-btn-neutral group flex items-center justify-between gap-3 text-left"
                        @click="logCtaClick(moment)">
                        <span class="details md:body-small">{{ moment.title }}</span>
                        <i class="bi bi-arrow-right transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true"></i>
                    </BaseLink>
                </div>
            </div>
        </BaseCard>
    </div>
</template>

<script setup lang="ts">
import HomeSectionHeader from "~vue/ChatHome/HomeSectionHeader.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import GoalSettingMark from "~vue/icons/GoalSettingMark.vue";
import BaseLink from "~vue/router/BaseLink.vue";
import type { PerformanceMoment } from "~vue/types/performance_management";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { tonalThemeEnabled } from "~vue/utils/theme";
import { DateTime } from "luxon";
import { computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";

interface Props {
    enabled: boolean;
    title: string;
    homepageCardTitle?: string;
    homepageCardSubtitle?: string;
    moments?: PerformanceMoment[];
}

const props = withDefaults(defineProps<Props>(), {
    homepageCardTitle: "",
    homepageCardSubtitle: "",
    moments: () => [],
});

const { t } = useI18n();

// A moment is "live" while today is within its date window, regardless of whether the
// user has completed its CTAs — always-on moments (e.g. year-round goal setting) should
// keep their button visible so users can return to refine or add goals (ENG-1330).
const liveMoments = computed(() => {
    const today = DateTime.now().toISODate();
    return props.moments.filter((moment) => moment.start_date <= today && moment.expiration_date > today);
});

const showCard = computed(() => props.enabled && liveMoments.value.length > 0);

const cardTitle = computed(() => props.homepageCardTitle || t("chat.home.performanceManagement.defaultTitle"));
const cardSubtitle = computed(() => props.homepageCardSubtitle || t("chat.home.performanceManagement.defaultSubtitle"));

const momentUrl = (moment: PerformanceMoment) => `/performance_management?moment_id=${moment.id}`;

onMounted(() => {
    if (showCard.value) {
        logUserInteraction(AmplitudeEventNames.MOMENTS_CARD_SHOWN, { source: "homepage_tile" });
    }
});

const logCtaClick = (moment: PerformanceMoment) => {
    logUserInteraction(AmplitudeEventNames.PERFORMANCE_REVIEW_CARD_CTA_CLICKED, {
        label: moment.title,
        url: momentUrl(moment),
        moment_id: moment.id,
    });
    logUserInteraction(AmplitudeEventNames.PERFORMANCE_REVIEW_ENTRY_CLICKED, { entry_source: "homepage_card" });
};
</script>
